import React, { useState } from 'react';
import { Button } from 'antd';
import type { ReportCardData } from '@ctd/agent-contract';
import { KpiCardShell } from '~analytics-components/kpi-card-shell';
import { useStore } from '~contexts/mobx';
import { formatCalendarDate, formatTorontoDate } from '../utils/card-format';
import {
    ReportUnauthorizedError,
    ReportUnavailableError,
    fetchReportFile,
    saveReportWorkbook,
    showReportPage,
} from '../utils/report-files';
import style from '../style.module.scss';

const UNAVAILABLE = 'This report is no longer available';
const POPUP_BLOCKED = 'Allow pop-ups to open the report';
const COULD_NOT_OPEN = "Couldn't open this report";

const count = (total: number, noun: string) =>
    `${total} ${noun}${total === 1 ? '' : 's'}`;

export const ReportCard: React.FC<{
    card: ReportCardData;
    fetchImpl?: typeof fetch;
}> = ({ card, fetchImpl }) => {
    const rootStore = useStore();
    const [busy, setBusy] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);

    const attempt = async (work: () => Promise<void>, abandon?: () => void) => {
        setBusy(true);
        setProblem(null);
        try {
            await work();
        } catch (error) {
            abandon?.();
            if (error instanceof ReportUnauthorizedError) {
                rootStore.handleUnauthorized(error.message || undefined);
            } else {
                setProblem(
                    error instanceof ReportUnavailableError
                        ? UNAVAILABLE
                        : COULD_NOT_OPEN
                );
            }
        } finally {
            setBusy(false);
        }
    };

    const fetchFile = (format: 'html' | 'xlsx') =>
        fetchReportFile(card.reportId, format, {
            headers: rootStore.getHeaders(),
            fetchImpl,
        });

    const openPage = async (action: 'open' | 'pdf') => {
        const target = window.open('', '_blank');
        if (!target) {
            setProblem(POPUP_BLOCKED);
            return;
        }
        await attempt(
            async () => {
                const { blob } = await fetchFile('html');
                showReportPage(target, blob, { print: action === 'pdf' });
            },
            () => target.close()
        );
    };

    const downloadWorkbook = () =>
        attempt(async () => {
            const { blob, filename } = await fetchFile('xlsx');
            saveReportWorkbook(blob, filename ?? `${card.title}.xlsx`);
        });

    const dates = [
        card.reportDate &&
            `Report dated ${formatCalendarDate(card.reportDate)}`,
        card.dataAsOf && `Data as of ${formatTorontoDate(card.dataAsOf)}`,
    ].filter(Boolean);

    const button = (label: string, onClick: () => unknown) => (
        <Button size="small" disabled={busy} onClick={onClick}>
            {label}
        </Button>
    );

    return (
        <div className={style.wide_card}>
            <KpiCardShell
                title={card.title}
                subtitle={`${count(card.sectionCount, 'section')} · ${count(
                    card.figureCount,
                    'figure'
                )}`}
                size="lg"
            >
                {card.figureCount === 0 && (
                    <p className={style.report_note}>No figures available</p>
                )}
                {card.omissionCount > 0 && (
                    <p className={style.report_warning}>
                        {`${count(card.omissionCount, 'figure')} not available`}
                    </p>
                )}
                <div className={style.report_actions}>
                    {button('Open', () => openPage('open'))}
                    {button('PDF', () => openPage('pdf'))}
                    {button('Excel', downloadWorkbook)}
                </div>
                {problem && (
                    <p className={style.report_error} role="alert">
                        {problem}
                    </p>
                )}
                {dates.length > 0 && (
                    <p className={style.card_footer}>{dates.join(' · ')}</p>
                )}
            </KpiCardShell>
        </div>
    );
};
